import { useTranslation } from 'react-i18next';
import type { Loop } from '@/common/api/types/loops';
import type { ObjectiveHealth } from '@/common/api/types/objectives';
import { FINDING_GROUP_ORDER } from '@/features/agentic-insights/components/data/groupFindings';
import type { PersonaView } from '@/features/agentic-insights/components/data/resolvePersonaView';
import type { FindingEntry } from '@/features/agentic-insights/components/experimental/shared/data/findingEntries';
import { entryKey } from '../data/aggregateFindings';
import { CONFIDENCE_TONE, GROUP_TONE, MEASURE, TYPE } from '../language';
import { FindingArgument } from './FindingArgument';
import { FindingHead } from './FindingHead';

interface DetailViewProps {
  /** Carries a `loop` per entry only in the All loops reading — see `LoopStamp`. */
  entries: (FindingEntry & { loop?: Loop })[];
  views: Map<string, PersonaView>;
  /** Keyed by objective id, for the meta line's objective-scope entry. Undefined while loading. */
  objectiveHealthById: Record<number, ObjectiveHealth> | undefined;
  isObjectiveHealthPending: boolean;
  selectedFindingId: string | undefined;
  onSwitchLoop?: (loopId: number) => void;
  onSelectFinding: (findingId: string) => void;
}

/**
 * One finding at a time, with the rest one click away in the rail.
 *
 * The same findings the list shows, read in full: the argument first — claim, what it rests on, what
 * to do — then the cross-examination of it. Not a separate reading of the run but the same one at
 * depth, which is why it lives under Findings rather than beside it.
 *
 * The rail exists because "is this sound?" is a question asked of several findings in a row, and
 * navigating out to the list and back in loses the thread each time.
 */
export const DetailView = ({
  entries,
  views,
  objectiveHealthById,
  isObjectiveHealthPending,
  selectedFindingId,
  onSwitchLoop,
  onSelectFinding,
}: DetailViewProps) => {
  const { t } = useTranslation();

  const selected = entries.find((entry) => entryKey(entry) === selectedFindingId) ?? entries[0];
  const view = selected ? views.get(entryKey(selected)) : undefined;

  if (!selected || view?.kind !== 'voiced') {
    return (
      <p data-testid="agentic-x-detail-empty" className={`${TYPE.body} ${MEASURE} py-12`}>
        {t(($) => $.loopAgenticInsights.experimental.audit.empty)}
      </p>
    );
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[15rem_1fr]">
      <nav
        data-testid="agentic-x-detail-rail"
        aria-label={t(($) => $.loopAgenticInsights.experimental.audit.railLabel)}
        className="flex max-h-[26rem] flex-col overflow-y-auto lg:max-h-none"
      >
        {FINDING_GROUP_ORDER.flatMap((group) => {
          const inGroup = entries.filter((entry) => entry.group === group);
          if (inGroup.length === 0) return [];

          return [
            <p
              key={`heading-${group}`}
              className={`${TYPE.section} ${GROUP_TONE[group]} border-t border-border-neutral-subtle pt-4 pb-1`}
            >
              {t(($) => $.loopAgenticInsights.group[group])}
            </p>,
            ...inGroup.map((entry) => {
              const key = entryKey(entry);
              const isSelected = key === entryKey(selected);
              const entryView = views.get(key);
              if (entryView?.kind !== 'voiced') return null;
              return (
                <button
                  key={key}
                  type="button"
                  aria-current={isSelected}
                  data-testid="agentic-x-detail-rail-item"
                  onClick={() => onSelectFinding(key)}
                  className={`flex items-start gap-2 py-3 pr-2 text-left transition-colors hover:bg-bg-neutral-subtle ${
                    isSelected ? 'bg-bg-brand-subtler' : ''
                  }`}
                >
                  <span className={`${TYPE.ordinal} w-5 shrink-0 text-right`}>{entry.ordinal}</span>
                  <span className="flex min-w-0 flex-col gap-1">
                    <span
                      className={`line-clamp-3 text-xs leading-snug ${isSelected ? 'font-medium text-text-neutral-primary' : 'text-text-neutral-secondary'}`}
                    >
                      {entryView.businessStatement}
                    </span>
                    <span className={`${TYPE.meta} ${CONFIDENCE_TONE[entry.finding.confidence]}`}>
                      {t(($) => $.loopAgenticInsights.confidence[entry.finding.confidence])}
                    </span>
                  </span>
                </button>
              );
            }),
          ];
        })}
      </nav>

      <article
        data-testid="agentic-x-detail"
        className="flex min-w-0 flex-col gap-10 border-t border-border-neutral-subtle pt-6 pb-16 lg:border-t-0 lg:pt-0"
      >
        <FindingHead
          finding={selected.finding}
          view={view}
          objectiveHealthById={objectiveHealthById}
          isObjectiveHealthPending={isObjectiveHealthPending}
          loop={selected.loop}
          onSwitchLoop={onSwitchLoop}
        />
        <FindingArgument finding={selected.finding} view={view} />
      </article>
    </div>
  );
};
